// src/ui/StoriesBar.jsx — Stories at the top of the browser's Vibes feed
// (the phone's twin is src/components/StoriesRow.tsx). "Create story" opens the
// file picker, then the SAME media editor a Vibe uses (filters, text, stickers,
// music, trim), then a small sheet to choose who sees it. The rings come from the
// server (storyRoutes.feed), grouped and ordered by services/stories.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MediaEditor from './MediaEditor';
import StoryViewer from './StoryViewer';
import { fetchStoryFeed } from '../services/storyApi';
import { publishStories } from '../services/vibes';
import { groupStories } from '../services/stories';
import { X } from './icons';

export default function StoriesBar({ me, onProfile }) {
  const [groups, setGroups] = useState([]);
  const [seen, setSeen] = useState({});
  const [viewAt, setViewAt] = useState(null);
  const [files, setFiles] = useState([]);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState(null);
  const [audience, setAudience] = useState('friends');
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState('');
  const raw = useRef([]);
  const seenRef = useRef({});
  const input = useRef(null);

  const load = useCallback(async () => {
    if (!me?.uid) return;
    const r = await fetchStoryFeed();
    if (!r || !r.ok) return;
    raw.current = Array.isArray(r.stories) ? r.stories : [];
    seenRef.current = { ...(r.seen || {}), ...seenRef.current };
    setSeen(seenRef.current);
    setGroups(groupStories(raw.current, me.uid, seenRef.current, Date.now()));
  }, [me?.uid]);
  useEffect(() => { load(); const iv = setInterval(load, 3 * 60 * 1000); return () => clearInterval(iv); }, [load]);

  const pick = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 10);
    e.target.value = '';
    if (!list.length) return;
    setFiles(list); setEdits(null); setEditing(true);
  };
  const share = async () => {
    const fl = files; const ed = edits || [];
    setEdits(null); setFiles([]);
    setBusy({ done: 0, total: fl.length });
    const r = await publishStories({ me, files: fl, edits: ed, audience, onProgress: (done, total) => setBusy({ done, total }) });
    setBusy(null);
    setNote(r.posted ? 'Your story is up for 24 hours' : 'Your story couldn’t be posted — try again');
    setTimeout(() => setNote(''), 2600);
    load();
  };

  const mine = groups.find((g) => g.mine);
  const others = groups.filter((g) => !g.mine);
  const ordered = mine ? [mine, ...others] : others;
  const card = (g) => {
    const last = g.stories[g.stories.length - 1] || {};
    const pic = last.type === 'video' ? last.thumbUrl : last.url;
    return (
      <button key={g.uid} className="vb-story" onClick={() => setViewAt(groups.indexOf(g))} title={g.mine ? 'Your story' : g.name}>
        {pic ? <img src={pic} alt="" loading="lazy" /> : <span className="vb-story-blank" />}
        <span className={`vb-story-ring ${g.allSeen && !g.mine ? 'seen' : ''}`}>
          {g.image ? <img src={g.image} alt="" /> : <b>{(g.name || '?').charAt(0)}</b>}
        </span>
        <span className="vb-story-name">{g.mine ? 'Your story' : g.name}</span>
      </button>
    );
  };

  return (
    <>
      <div className="vb-stories">
        <button className="vb-story add" onClick={() => !busy && input.current?.click()} disabled={!!busy}>
          {/* a round picture fills the square box: blurred behind, enlarged in front (see the phone's StoriesRow) */}
          {me?.profileImage ? (
            <span className="vb-story-me" aria-hidden="true">
              <img className="bg" src={me.profileImage} alt="" />
              <img className="fg" src={me.profileImage} alt="" />
            </span>
          ) : <span className="vb-story-blank" />}
          <span className="vb-story-plus">{busy ? '…' : '+'}</span>
          <span className="vb-story-name dark">{busy ? `Posting ${Math.min(busy.total, busy.done + 1)}/${busy.total}…` : 'Create story'}</span>
        </button>
        {ordered.map(card)}
        <input ref={input} type="file" accept="image/*,video/*" multiple hidden onChange={pick} />
      </div>
      {note && <div className="vb-story-note">{note}</div>}

      {editing && files.length > 0 && (
        <MediaEditor files={files} initial={edits} me={me} caption=""
          onCancel={() => { setEditing(false); setFiles([]); }}
          onDone={(next) => { setEdits(next); setEditing(false); }} />
      )}
      {!editing && edits && files.length > 0 && (
        <div className="vb-fixed-share" onClick={() => { setEdits(null); setFiles([]); }}>
          <div className="vb-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Share to story">
            <div className="vb-sheet-head">
              <b>Share to story</b>
              <button className="icon-btn" onClick={() => { setEdits(null); setFiles([]); }} aria-label="Close"><X width={18} height={18} /></button>
            </div>
            <div className="vb-sheet-body form">
              <div className="vb-sub">{files.length === 1 ? '1 photo or video' : `${files.length} photos and videos`} · visible for 24 hours</div>
              <div className="vb-seg" role="group" aria-label="Who can see it">
                <span className="vb-seg-label">Visible to</span>
                <button className={audience === 'friends' ? 'on' : ''} onClick={() => setAudience('friends')}>Friends</button>
                <button className={audience === 'everyone' ? 'on' : ''} onClick={() => setAudience('everyone')}>Everyone</button>
              </div>
              <button className="vb-tagchip" onClick={() => setEditing(true)}>Edit again</button>
            </div>
            <div className="vb-sheet-foot"><button className="lg-go" onClick={share}>Share to story</button></div>
          </div>
        </div>
      )}
      {viewAt !== null && groups[viewAt] && (
        <StoryViewer
          groups={groups} startGroup={viewAt} seen={seen} me={me}
          onClose={() => { setViewAt(null); setGroups(groupStories(raw.current, me.uid, seenRef.current, Date.now())); }}
          onSeen={(id) => { if (!seenRef.current[id]) { seenRef.current = { ...seenRef.current, [id]: Date.now() }; setSeen(seenRef.current); } }}
          onProfile={(g) => { setViewAt(null); onProfile && onProfile({ uid: g.uid, name: g.name, image: g.image }); }}
          onDeleted={(id) => { raw.current = raw.current.filter((x) => x.id !== id); setGroups(groupStories(raw.current, me.uid, seenRef.current, Date.now())); }}
        />
      )}
    </>
  );
}
